import { useMemo } from 'react'

import { type PimProductV1Flags } from '@redteclab/api/clients/bully'

import {
  type AlgoliaProxyRecord,
  algoliaRecordAccessorGetCurrentTimeIsInRedpointsTimeframe,
  algoliaRecordAccessorGetPromotionType,
  algoliaRecordAccessorGetRedPointsMultiplier,
  algoliaRecordAccessorNowProductGroupIsSameDayNextDay,
  type PromotionType,
} from '../../../../algolia'
import { useIsNowEnabled } from '../../../../now'

type ProductSearchResultItemLabelType =
  // active flags from product flags
  | keyof PimProductV1Flags
  // if offer supports now
  | 'now'
  // product is out of sale / has specific stock statuses
  | 'outOfSale'
  // if offer has promotion
  | 'promotion'
  // promotionSet - aka sparset - flag on offer
  | 'promotionSet'
  // if offer has redpoints multiplier and multiplier is greater than 1
  | 'redPointsMultiplier'
  // if the offer has a seller (marketplace) driven promotion
  | 'sellerPromotion'
  // retail media sponsored product
  | 'sponsored'
  // volume based pricing
  | 'volumeBasedPricing'

export interface ProductSearchResultItemLabel {
  labelText?: string
  promotionSpecific?: {
    promotionType: PromotionType
  }
  redPointsMultiplierSpecific?: {
    multiplier: number
  }
  type: ProductSearchResultItemLabelType
}

const TWO = 2

const getRedPointsMultiplierLabel = (
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  const offer = product.best_offer

  if (!offer?.redpoints) {
    return undefined
  }

  if (!algoliaRecordAccessorGetCurrentTimeIsInRedpointsTimeframe(product)) {
    return undefined
  }

  const multiplier = algoliaRecordAccessorGetRedPointsMultiplier(product)

  if (multiplier < TWO) {
    return undefined
  }

  return {
    labelText: 'multiplier',
    redPointsMultiplierSpecific: {
      multiplier,
    },
    type: 'redPointsMultiplier',
  }
}

const getNowLabel = (
  isNowEnabled: boolean,
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  const offer = product.best_offer

  if (
    !isNowEnabled ||
    !offer ||
    product.prescriptionMedicine ||
    !algoliaRecordAccessorNowProductGroupIsSameDayNextDay(product)
  ) {
    return undefined
  }

  return {
    type: 'now',
  }
}
const getSellerPromotionLabel = (
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  if (product.hasBestOfferSellerPromotion) {
    return {
      type: 'sellerPromotion',
    }
  }

  return undefined
}
const getPromotionLabel = (
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  const promotionType = algoliaRecordAccessorGetPromotionType(product)

  if (!promotionType) {
    return undefined
  }

  return {
    promotionSpecific: {
      promotionType,
    },
    type: 'promotion',
  }
}
const getSponsoredLabel = (
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  if (product.adServerMetadata) {
    return {
      type: 'sponsored',
    }
  }

  return undefined
}

const getVolumeBasedPricingLabel = (
  product: AlgoliaProxyRecord,
): ProductSearchResultItemLabel | undefined => {
  const bestOffer = product.best_offer

  if (!bestOffer) {
    return undefined
  }

  const hasVolumePricing = Boolean(
    bestOffer.volume_based_prices && bestOffer.volume_based_prices.length > 1,
  )

  if (hasVolumePricing) {
    return {
      type: 'volumeBasedPricing',
    }
  }

  return undefined
}

const labelOrder = [
  'sponsored',
  'prescription_medicine',
  'volumeBasedPricing',
  'redPointsMultiplier',
  'sellerPromotion',
  'promotion',
  'now',
  'outOfSale',
]

const getLabelsFromAtributes = (
  algoliaRecord: AlgoliaProxyRecord,
): ProductSearchResultItemLabel[] => {
  /**
   * check the labels that should be skipped
   * rezeptpflichtig will be added again if prescriptionMedicine
   * we need to filter the subscription flag, because subscriptions are being dropped off from the shop
   * ABO label is deprecated
   */
  const SKIP_LABELS = new Set([
    'ABO',
    'product_subscription',
    'rezeptpflichtig',
  ])

  const labels = [...algoliaRecord.attributes]

  if (algoliaRecord.prescriptionMedicine) {
    labels.push('prescription_medicine')
  }

  return labels
    .filter((flag) => !SKIP_LABELS.has(flag))
    .map((flag): ProductSearchResultItemLabel => {
      return {
        labelText: flag,
        type: flag as ProductSearchResultItemLabel['type'],
      }
    })
}

const removeDuplicateTextLabels = (
  labels: ProductSearchResultItemLabel[],
): ProductSearchResultItemLabel[] => {
  const existing: Record<string, ProductSearchResultItemLabel | undefined> = {}

  return labels.filter((label) => {
    if (!label.labelText) {
      return true
    }
    // same label was met before
    if (existing[label.labelText]) {
      return false
    }

    // add label to existing by text, so we can filter next if it will have the same one.
    existing[label.labelText] = label

    return true
  })
}

export const useProductSearchResultItemLabels = ({
  algoliaRecord,
}: {
  algoliaRecord: AlgoliaProxyRecord
}): ProductSearchResultItemLabel[] => {
  const isNowEnabled = useIsNowEnabled()

  return useMemo((): ProductSearchResultItemLabel[] => {
    const labels: ProductSearchResultItemLabel[] = []

    labels.push(...getLabelsFromAtributes(algoliaRecord))

    for (const label of [
      getRedPointsMultiplierLabel(algoliaRecord),
      getNowLabel(isNowEnabled, algoliaRecord),
      getSellerPromotionLabel(algoliaRecord),
      getPromotionLabel(algoliaRecord),
      getSponsoredLabel(algoliaRecord),
      getVolumeBasedPricingLabel(algoliaRecord),
    ]) {
      if (label) {
        labels.push(label)
      }
    }

    labels.sort((flagA, flagB) => {
      const indexA = labelOrder.indexOf(flagA.type)
      const indexB = labelOrder.indexOf(flagB.type)

      if (indexA === indexB) {
        return 0
      }

      return labelOrder.indexOf(flagA.type) > labelOrder.indexOf(flagB.type)
        ? -1
        : 1
    })

    /**
     * https://jira.shop-apotheke.com/browse/WSAWA-3871
     * edgecase when labels are same for different flags, filter to prevent double display.
     * Known case is FR
     * "medicament": {
     *    "active": true,
     *    "label": "Médicament" // <---
     * },
     * "pharma_obl": {
     *   "active": true,
     *   "label": "Médicament" // <---
     * }
     */
    return removeDuplicateTextLabels(labels)
  }, [algoliaRecord, isNowEnabled])
}
